/**
 * PivotChip: the cross-entity pivot in the /admin portal ("5 tenants",
 * "12 people", an organization's name). Clicking it opens the other tab
 * filtered to this entity, so it is a link-weight ghost button with a
 * trailing arrow: "this goes somewhere", in the one interactive color.
 *
 * Callers pass an already-composed label; the arrow is drawn by the button.
 * A table cell whose header already names the subject can show less
 * (`display`, e.g. the bare count) while `label` stays the accessible name.
 */
import type { ReactNode } from 'react'
import { Button } from '@/components/carbon/Button'
import { IconArrowRight } from '@/components/carbon/icons'

export interface PivotChipProps {
  /** Already-composed label including count + subject, e.g. "5 tenants". */
  label: string
  onClick(): void
  ariaLabel?: string
  /** What the chip shows, when it differs from `label` (a count under a column header that names the subject). */
  display?: ReactNode
}

export default function PivotChip({ label, onClick, ariaLabel, display }: PivotChipProps) {
  return (
    <Button
      kind="ghost"
      size="sm"
      icon={<IconArrowRight />}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      wrap={false}
      className="-ml-3 max-w-full"
    >
      {display ?? label}
    </Button>
  )
}
